"use client";

import { useState, useSyncExternalStore } from "react";

/**
 * Состояние серверного расчёта таблицы — общее для таблицы и слоя данных.
 *
 * Зачем понадобилось. Слепок заказов (5,26 МБ на «Скромном») качать незачем,
 * если нужные поля приезжают посчитанными с сервера. Но решение «качать или
 * нет» принимает слой данных, а знает о готовом ответе таблица. Первая попытка
 * связать их «по флагу в адресе» кончилась пустой таблицей: переключатель был
 * включён, а готовые данные не пришли — и слепка уже не было, и замены ещё.
 *
 * Поэтому состояний четыре, и запасной путь отключается ТОЛЬКО пока основной
 * жив:
 *
 *   off     — переключателя нет, всё как всегда;
 *   pending — таблица запросила готовые значения, ждём. Слепок не грузим:
 *             иначе смысл теряется, а окно ожидания короткое;
 *   ready   — готовые значения пришли, слепок не нужен;
 *   failed  — не пришли (ошибка, пустой ответ, старый формат хранения).
 *             Слепок грузится, таблица работает по-старому.
 *
 * Хранилище модульное, без провайдера: слой данных стоит в дереве выше таблицы,
 * и протаскивать состояние пропсами через всё приложение было бы хуже.
 */
export type PreparedOrdersStatus = "off" | "pending" | "ready" | "failed";

let status: PreparedOrdersStatus = "off";
const listeners = new Set<() => void>();

export function setPreparedOrdersStatus(next: PreparedOrdersStatus): void {
  if (status === next) return;
  status = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

const getSnapshot = () => status;
/** На сервере отрисовки состояния нет — там всегда обычный путь. */
const getServerSnapshot = (): PreparedOrdersStatus => "off";

export function usePreparedOrdersStatus(): PreparedOrdersStatus {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Слепок заказов не нужен, пока серверный путь жив. */
export function preparedOrdersCoverFetch(current: PreparedOrdersStatus): boolean {
  return current === "pending" || current === "ready";
}

/**
 * Источники данных, которые закрывает серверный расчёт. Пока один: заказы.
 * По мере роста покрытия сюда добавятся цены, характеристики и остальные.
 */
export const PREPARED_COVERED_SOURCES: readonly string[] = ["orders"];

/**
 * Признак `?prepared=1`, прочитанный в ПЕРВОМ рендере.
 *
 * Читать эффектом нельзя: план данных страницы объявляет спрос до того, как
 * эффекты успеют выполниться, и запрос сырья уходит раньше, чем признак
 * появится. Замер на боевой сборке 2026-09-08 это показал прямо.
 */
export function usePreparedFlag(): boolean {
  const [enabled] = useState(() =>
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("prepared") === "1");
  return enabled;
}
